import { ApolloClient, gql, NormalizedCacheObject, useQuery, WatchQueryFetchPolicy } from '@apollo/client'
import { GetBrandsDiscoveryQuery as Query, GetBrandsDiscoveryQueryVariables as Variables } from 'types/graphql'
import { Params } from '../context/BrandDiscoveryContext'
import { defaultContext } from '../controllers/brandDiscoveryMachine'

export const brandRowFieldsFragment = gql`
  fragment BrandRowFields on Brand {
    id
    title
    slug
    rip
    description
    primaryImageFullPath
    webUrl
    hasDiscountCodes
    instagramReviewUrl
    primaryImageProminentColorHex
    likesCount
    isPromoted
    milestones {
      type
      completedAt
    }
    status
    myReview {
      id
    }
    lists {
      id
      type
    }
    reviewSummary {
      brandId
      averageRating
      ratingsCount
    }
    categories {
      id
      slug
      name
      parentId
    }
  }
`

export const getBrandsDiscoveryQuery = gql`
  query GetBrandsDiscovery(
    $take: Int
    $skip: Int
    $exclude: [ID!]
    $order: BrandSortOrder
    $category: String
    $filters: [String!]
    $demographics: [String!]
    $shippingDestinations: [String!]
    $searchQuery: String
    $includePromotions: Boolean
    $includeDismissed: Boolean
  ) {
    brands(
      take: $take
      skip: $skip
      exclude: $exclude
      order: $order
      category: $category
      filters: $filters
      demographics: $demographics
      shippingDestinations: $shippingDestinations
      searchQuery: $searchQuery
      includePromotions: $includePromotions
      includeDismissed: $includeDismissed
    ) @connection(key: "brands", filter: ["order", "category", "filters", "demographics", "shippingDestinations", "searchQuery"]) {
      items {
        ...BrandRowFields
      }
      queryID
      total
      hasMore
      nextSkip
    }
  }
  ${brandRowFieldsFragment}
`

interface Options {
  variables?: Variables
  fetchPolicy?: WatchQueryFetchPolicy
  onCompleted?: (data: Query) => void
}
export const useBrandsDiscovery = ({ variables, fetchPolicy, onCompleted }: Options = {}) => {
  return useQuery<Query, Variables>(getBrandsDiscoveryQuery, {
    fetchPolicy,
    variables,
    notifyOnNetworkStatusChange: true,
    onCompleted
  })
}

export const paramsToDiscoveryVariables = (filterParams: Partial<Params>): Variables => {
  return {
    ...filterParams,
    category: filterParams.category ?? defaultContext.category,
    filters: filterParams.filters ?? defaultContext.filters,
    demographics: filterParams.demographics ?? defaultContext.demographics,
    shippingDestinations: filterParams.shippingDestinations ?? defaultContext.shippingDestinations,
    order: filterParams.order?.key ?? defaultContext.order.key
  }
}

export const queryBrandsDiscoveryWithClient = async (apolloClient: ApolloClient<NormalizedCacheObject>, variables: Variables) => {
  return apolloClient.query<Query, Variables>({
    query: getBrandsDiscoveryQuery,
    variables
  })
}
